import React, { Component } from "react";
import "../InTable/InTable.css";
import "./GenericPaginatedTable.scss";
import Dom from "../../../../helpers/Dom";

const ROW_WINDOW = 50;

class GenericPaginatedTable extends Component {
  constructor(props) {
    super(props);
    this.mounted = false;
    this.pageCache = {};
    this.pendingPages = {};
    this.scrollRef = null;
    this.filterMenuRef = null;
    this.scrollBound = false;

    this.state = {
      busy: false,
      body: [],
      items: [],
      startIndex: ROW_WINDOW,
      pageIndex: 0,
      pageCount: 0,
      visitedPages: [0],
      search: "",
      columnFilters: {},
      openFilterKey: null,
      activeFilterId: null,
      sortBy: null,
      query: props.query || {},
    };
  }

  componentDidMount() {
    this.mounted = true;
    document.addEventListener("mousedown", this.handleOutsideClick);
    if (!this.state.body.length) {
      this.loadPage(0);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.scrollRef && !this.scrollBound) {
      this.scrollRef.addEventListener("scroll", this.handleScroll);
      this.scrollBound = true;
    }

    const prevQuery = JSON.stringify(prevProps.query || {});
    const nextQuery = JSON.stringify(this.props.query || {});
    if (prevQuery !== nextQuery) {
      this.pageCache = {};
      this.pendingPages = {};
      this.setState({ query: this.props.query || {} }, () => {
        if (this.state.activeFilterId) {
          this.fetchFiltered(this.state.activeFilterId);
        } else {
          this.loadPage(this.state.pageIndex, true);
        }
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    document.removeEventListener("mousedown", this.handleOutsideClick);
    if (this.scrollRef) {
      this.scrollRef.removeEventListener("scroll", this.handleScroll);
    }
  }

  handleOutsideClick = (event) => {
    if (this.filterMenuRef && !this.filterMenuRef.contains(event.target)) {
      this.setState({ openFilterKey: null });
    }
  };

  getPageSize = () => this.props.pageSize || 100;

  resolvePageCount = () => {
    if (this.props.getPageCount) {
      return this.props.getPageCount() || 0;
    }
    return this.state.pageCount || 0;
  };

  getHeads = () => (this.props.data && this.props.data.head) || [];

  getRowKey = (row, index) => {
    if (this.props.getRowKey) {
      return this.props.getRowKey(row, index);
    }
    return row.uid != null ? row.uid : row.vehicleno || index;
  };

  mapRows = (rawResp) => {
    const mapper = this.props.mapResponse;
    if (!mapper) return Promise.resolve(Array.isArray(rawResp) ? rawResp : []);
    const result = mapper(rawResp);
    return result && typeof result.then === "function"
      ? result
      : Promise.resolve(result);
  };

  getVisibleRows = (body = this.state.body) => {
    const search = (this.state.search || "").trim().toLowerCase();
    const filters = this.state.columnFilters || {};
    let rows = body.filter((row) => {
      for (const key in filters) {
        if (
          filters[key] &&
          String(row[key] != null ? row[key] : "") !== String(filters[key])
        ) {
          return false;
        }
      }
      if (!search) return true;
      return Object.keys(row).some((key) =>
        String(row[key] != null ? row[key] : "")
          .toLowerCase()
          .includes(search)
      );
    });

    const sortBy = this.state.sortBy;
    if (sortBy && sortBy.key) {
      const action = sortBy.ascOrder ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const av = a[sortBy.key];
        const bv = b[sortBy.key];
        if (typeof av === "string" || typeof bv === "string") {
          if (String(av) < String(bv)) return -1 * action;
          if (String(av) > String(bv)) return 1 * action;
          return 0;
        }
        return (bv - av) * action;
      });
    }
    return rows;
  };

  getVisibleData = () => this.getVisibleRows();

  applyView = (body, extraState = {}) => {
    const nextBody = body || [];
    const visible = this.getVisibleRows(nextBody);
    this.setState(
      {
        body: nextBody,
        items: visible.slice(0, ROW_WINDOW),
        startIndex: ROW_WINDOW,
        busy: false,
        ...extraState,
      },
      () => {
        if (this.scrollRef) this.scrollRef.scrollTop = 0;
      }
    );
  };

  refreshView = () => {
    const visible = this.getVisibleRows();
    this.setState(
      {
        items: visible.slice(0, ROW_WINDOW),
        startIndex: ROW_WINDOW,
      },
      () => {
        if (this.scrollRef) this.scrollRef.scrollTop = 0;
      }
    );
  };

  setData = (data = []) => {
    if (!this.mounted) return;
    this.pageCache[0] = data;
    this.applyView(data, {
      pageIndex: 0,
      pageCount: this.resolvePageCount(),
      visitedPages: Array.from(new Set([0, ...this.state.visitedPages])),
      activeFilterId: null,
    });
  };

  setBusy = (busy) => {
    if (!this.mounted) return;
    this.setState({ busy: !!busy });
  };

  loadPage = (pageIndex, force = false) => {
    if (this.state.activeFilterId) return;
    if (!this.props.fetchPage) return;

    const pageCount = this.resolvePageCount();
    const pageSize = this.getPageSize();

    if (!force && pageIndex in this.pageCache) {
      this.applyView(this.pageCache[pageIndex], {
        pageIndex,
        pageCount,
        visitedPages: Array.from(
          new Set([...this.state.visitedPages, pageIndex])
        ),
      });
      return;
    }

    if (this.pendingPages[pageIndex]) return;
    this.pendingPages[pageIndex] = true;

    this.setState({ busy: true, pageIndex, pageCount });
    this.props
      .fetchPage({
        limit: pageSize,
        offset: pageIndex * pageSize,
        query: this.state.query,
      })
      .then(
        (resp) =>
          this.mapRows(resp).then((data) => {
            delete this.pendingPages[pageIndex];
            if (!this.mounted) return;
            this.pageCache[pageIndex] = data;
            this.applyView(data, {
              pageIndex,
              pageCount: this.resolvePageCount(),
              visitedPages: Array.from(
                new Set([...this.state.visitedPages, pageIndex])
              ),
            });
          }),
        () =>
          this.mapRows([]).then((data) => {
            delete this.pendingPages[pageIndex];
            if (!this.mounted) return;
            this.applyView(data, {
              pageIndex,
              pageCount: this.resolvePageCount(),
            });
          })
      );
  };

  fetchFiltered = (filterId) => {
    if (!filterId) {
      const pageIndex = this.state.pageIndex || 0;
      if (pageIndex in this.pageCache) {
        this.applyView(this.pageCache[pageIndex], {
          pageIndex,
          pageCount: this.resolvePageCount(),
          activeFilterId: null,
        });
        return;
      }
      this.loadPage(pageIndex, false);
      return;
    }

    if (!this.props.fetchByVehicle) return;

    this.setState({ busy: true, activeFilterId: filterId });
    this.props
      .fetchByVehicle({
        vehicleids: Array.isArray(filterId) ? filterId : [filterId],
        query: this.state.query,
      })
      .then(
        (resp) =>
          this.mapRows(resp).then((data) => {
            if (!this.mounted) return;
            this.applyView(data, { activeFilterId: filterId });
          }),
        () =>
          this.mapRows([]).then((data) => {
            if (!this.mounted) return;
            this.applyView(data, { activeFilterId: filterId });
          })
      );
  };

  selectVehicle = (vehicleId) => {
    this.setState({ activeFilterId: vehicleId || null }, () => {
      this.fetchFiltered(vehicleId || null);
    });
  };

  setQuery = (query, reload = true) => {
    this.setState({ query: query || {} }, () => {
      if (!reload) return;
      this.pageCache = {};
      this.pendingPages = {};
      if (this.state.activeFilterId) {
        this.fetchFiltered(this.state.activeFilterId);
      } else {
        this.loadPage(this.state.pageIndex, true);
      }
    });
  };

  changePage = (pageIndex) => {
    if (pageIndex === this.state.pageIndex) return;
    this.loadPage(pageIndex);
  };

  handleSearch = (event) => {
    this.setState({ search: event.target.value }, this.refreshView);
  };

  toggleSort = (head) => {
    if (!head || (head.filterable && this.state.openFilterKey === head.key)) {
      return;
    }
    const current = this.state.sortBy;
    const next = {
      key: head.key,
      ascOrder:
        current && current.key === head.key ? !current.ascOrder : true,
    };
    this.setState({ sortBy: next }, this.refreshView);
  };

  toggleFilterMenu = (event, head) => {
    event.stopPropagation();
    this.setState({
      openFilterKey:
        this.state.openFilterKey === head.key ? null : head.key,
    });
  };

  setColumnFilter = (key, value) => {
    const columnFilters = { ...this.state.columnFilters };
    if (!value) {
      delete columnFilters[key];
    } else {
      columnFilters[key] = value;
    }
    this.setState({ columnFilters, openFilterKey: null }, this.refreshView);
  };

  getFilterValues = (key) => {
    const values = (this.state.body || [])
      .map((row) => row[key])
      .filter((value) => value != null && String(value).trim() !== "");
    return Array.from(new Set(values.map((value) => String(value)))).sort();
  };

  handleScroll = () => {
    if (!this.scrollRef) return;
    const { scrollTop, scrollHeight, clientHeight } = this.scrollRef;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      this.loadMore();
    }
  };

  loadMore = () => {
    const visible = this.getVisibleRows();
    const { startIndex, items } = this.state;
    if (!items.length || startIndex >= visible.length) return;
    const endIndex = Math.min(startIndex + ROW_WINDOW, visible.length);
    this.setState({
      items: [...items, ...visible.slice(startIndex, endIndex)],
      startIndex: startIndex + ROW_WINDOW,
    });
  };

  getPagination = () => {
    const count = this.state.pageCount;
    if (!count) return [];
    const itemWidth = 40;
    const width =
      (this.props.data &&
        this.props.data._position &&
        this.props.data._position.width) ||
      800;
    const itemAllowed = Math.max(3, Math.floor((width - 208) / itemWidth));
    if (count <= itemAllowed) {
      return Array.from({ length: count }, (_, idx) => idx);
    }
    let mid = Math.ceil(this.state.pageIndex - itemAllowed / 2);
    mid = Math.max(0, Math.min(mid, count - Math.floor(itemAllowed)));
    const pag = [];
    for (let idx = mid + 1; idx < mid + itemAllowed - 2; idx++) {
      pag.push(idx);
    }
    return [0, ...pag, count - 1];
  };

  renderFilterMenu(head) {
    if (this.state.openFilterKey !== head.key) return null;
    const values = this.getFilterValues(head.key);
    const active = this.state.columnFilters[head.key];
    return (
      <div
        className="gpt-filter-menu"
        ref={(ref) => (this.filterMenuRef = ref)}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={!active ? "is-active" : ""}
          onClick={() => this.setColumnFilter(head.key, null)}
        >
          All
        </button>
        {values.map((value) => (
          <button
            type="button"
            key={value}
            className={active === value ? "is-active" : ""}
            onClick={() => this.setColumnFilter(head.key, value)}
          >
            {value}
          </button>
        ))}
        {!values.length ? (
          <div className="gpt-filter-empty">No values</div>
        ) : null}
      </div>
    );
  }

  render() {
    const heads = this.getHeads();
    const visible = this.state.items;
    const filterMode = !!this.state.activeFilterId;
    const hasData = this.state.body.length > 0;
    const visitedClass =
      this.props.visitedPageClassName || "itpi-name-pageClicked";

    return (
      <div
        className={
          "InTable -white GenericPaginatedTable " +
          (this.props.className || "")
        }
      >
        {this.state.busy ? <div className="it-spinner" /> : null}
        {!this.state.busy && !hasData ? (
          <div className="it-no-data">
            {this.props.noDataMsg || "No Data"}
          </div>
        ) : null}
        <table>
          <thead>
            <tr>
              {heads.map((head) => (
                <th
                  key={head.key}
                  onClick={() => this.toggleSort(head)}
                  style={head.width ? { width: `${head.width}%` } : undefined}
                >
                  {this.state.sortBy && this.state.sortBy.key === head.key ? (
                    <i
                      className={
                        this.state.sortBy.ascOrder
                          ? "fa fa-sort-amount-asc"
                          : "fa fa-sort-amount-desc"
                      }
                    />
                  ) : null}
                  {head.name}
                  {head.filterable ? (
                    <span
                      className={
                        "gpt-filter-btn" +
                        (this.state.columnFilters[head.key] ? " is-on" : "")
                      }
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => this.toggleFilterMenu(event, head)}
                    >
                      <i className="fa fa-filter" aria-hidden="true" />
                    </span>
                  ) : null}
                  {head.filterable ? this.renderFilterMenu(head) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            ref={(ref) => (this.scrollRef = ref)}
            className={filterMode ? "is-filter-mode" : ""}
          >
            {visible.map((row, index) => (
              <tr key={this.getRowKey(row, index)}>
                {heads.map((head) => (
                  <td key={head.key}>
                    {row[head.key] != null ? row[head.key] : "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!filterMode ? (
          <div className="it-pagination">
            <input
              placeholder="Search"
              value={this.state.search}
              onChange={this.handleSearch}
            />
            {this.getPagination().map((item) => (
              <div className="itp-item" key={item}>
                <div
                  className={Dom.Class(
                    {
                      active: item === this.state.pageIndex,
                      [visitedClass]: this.state.visitedPages.includes(item),
                    },
                    "itpi-name"
                  )}
                  onClick={() => this.changePage(item)}
                >
                  {item + 1}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="it-pagination gpt-pagination-search">
            <input
              placeholder="Search"
              value={this.state.search}
              onChange={this.handleSearch}
            />
          </div>
        )}
      </div>
    );
  }
}

export default GenericPaginatedTable;
